import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

export const MenuPermissionGuard: CanActivateFn = (route, state) => {
  const router: Router = inject(Router);
  const userService = inject(UserService);

  // Check if user data is available synchronously first
  const currentUser = userService.currentUser;
  if (currentUser) {
    return checkPermission(currentUser, route, router);
  }

  // If not available, wait for user data
  return userService.user$.pipe(
    map((user) => {
      return checkPermission(user, route, router);
    }),
    catchError((error) => {
      console.error('❌ MenuPermissionGuard error:', error);
      return of(router.parseUrl('/sign-in'));
    }),
  );
};

/**
 * Check permission for a user and route
 */
function checkPermission(user: any, route: any, router: Router): any {
  if (!user) {
    return router.parseUrl('/sign-in');
  }

  // console.log('👤 User data:', user);
  // console.log('👤 User roles:', user.roles);
  // console.log('👤 User permissions:', user.permissions);

  // Check if user has permission for this route
  const requiredPermission = route.data?.['permission'];
  if (!requiredPermission) {
    return true;
  }

  // Check if user has super admin role (full access) - CHECK THIS FIRST
  const roleNames = user.roles?.map((role: any) => role.role?.toLowerCase() || '') || [];
  const isSuperAdmin = roleNames.includes('super admin') || roleNames.includes('superadmin');

  if (isSuperAdmin) {
    return true;
  }

  // Check direct permission
  const hasDirectPermission = user.permissions?.some(
    (p: any) => p.key === requiredPermission && p.isAllowed,
  );

  if (hasDirectPermission) {
    return true;
  }

  // Page-level keys (e.g. administration.e-invoice.list) grant the feature route
  const hasChildPermission = user.permissions?.some(
    (p: any) =>
      p.isAllowed &&
      typeof p.key === 'string' &&
      p.key.startsWith(`${requiredPermission}.`),
  );

  if (hasChildPermission) {
    return true;
  }

  // Check hierarchical permissions (parent permissions)
  const hasHierarchicalPermission = checkHierarchicalPermission(user, requiredPermission);

  if (hasHierarchicalPermission) {
    return true;
  }

  return router.parseUrl('/unauthorized');
}

/**
 * Check if user has hierarchical permission (parent permissions grant access to children)
 */
function checkHierarchicalPermission(user: any, requiredPermission: string): boolean {
  // Check if user has super admin role (full access) - CHECK THIS FIRST
  const roleNames = user.roles?.map((role: any) => role.role?.toLowerCase() || '') || [];
  const isSuperAdmin = roleNames.includes('super admin') || roleNames.includes('superadmin');

  if (isSuperAdmin) {
    return true;
  }

  if (!user.permissions) return false;

  // Extract parent permission from the required permission
  const permissionParts = requiredPermission.split('.');

  // Check module-level permission (e.g., 'masters' for 'masters.customers')
  if (permissionParts.length >= 2) {
    const modulePermission = permissionParts[0];
    const hasModulePermission = user.permissions.some(
      (p: any) => p.key === modulePermission && p.isAllowed,
    );

    if (hasModulePermission) {
      return true;
    }
  }

  // Check feature-level permission (e.g., 'masters.customers' for 'masters.customers.view')
  if (permissionParts.length >= 3) {
    const featurePermission = `${permissionParts[0]}.${permissionParts[1]}`;
    const hasFeaturePermission = user.permissions.some(
      (p: any) => p.key === featurePermission && p.isAllowed,
    );

    if (hasFeaturePermission) {
      return true;
    }
  }

  return false;
}
